import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { PlusMinusInput } from 'aem-libs-react-ts/src/components/Inputs'
import RangeSlider from 'aem-libs-react-ts/src/components/Inputs/RangeSlider'
import TipsRotator from 'aem-libs-react-ts/src/components/TipsRotator'
import { thousandsFormatter } from 'aem-libs-react-ts/src/utils/Formatters'
import { InvestorTypeValue } from 'aem-libs-regular-investment/src/services/types'

import { calculateSavings } from '../actions/actionCreators'
import { RF_SAVINGS_INPUTS, RF_TABS } from '../constants'
import { contextSelector, savingsSelector } from '../store/selectors'
import InvestorSelect from './InvestorSelect'

const SavingsInputs = () => {
  const { t } = useTranslation()

  const {
    userTracking,
    paramsStatic: {
      savingsCalc: {
        defaultAmount,
        maxAmount,
        minAmount,
        stepAmount,
        defaultDuration,
        maxDuration,
        minDuration,
        stepDuration
      }
    }
  } = useSelector(contextSelector)

  const { savingsInputs } = useSelector(savingsSelector)

  const dispatch = useDispatch()

  const [currentAmount, setCurrentAmount] = useState(savingsInputs.amount)
  const [currentDuration, setCurrentDuration] = useState(savingsInputs.duration)

  useEffect(() => {
    dispatch(calculateSavings(savingsInputs))
  }, [])

  const handleInvestmentTypeChange = (type: InvestorTypeValue) =>
    dispatch(calculateSavings({ ...savingsInputs, investorType: type }))

  const handleAmountChange = (value: number) => {
    setCurrentAmount(value)
    dispatch(calculateSavings({ ...savingsInputs, amount: value }))
  }

  const handleCurrentAmountChange = (value: number) => setCurrentAmount(value)

  const handleMinusAmount = () => {
    const value = savingsInputs.amount - stepAmount
    setCurrentAmount(value)
    dispatch(calculateSavings({ ...savingsInputs, amount: value }))
  }

  const handlePlusAmount = () => {
    const value = savingsInputs.amount + stepAmount
    setCurrentAmount(value)
    dispatch(calculateSavings({ ...savingsInputs, amount: value }))
  }

  const handleCurrentDurationChange = (value: number) => setCurrentDuration(value)

  const handleDurationChange = (value: number) => {
    setCurrentDuration(value)
    dispatch(calculateSavings({ ...savingsInputs, duration: value }))
  }

  const handleMinusDuration = () => {
    const value = savingsInputs.duration - stepDuration
    setCurrentDuration(value)
    dispatch(calculateSavings({ ...savingsInputs, duration: value }))
  }

  const handlePlusDuration = () => {
    const value = savingsInputs.duration + stepDuration
    setCurrentDuration(value)
    dispatch(calculateSavings({ ...savingsInputs, duration: value }))
  }

  const rangeAmountParams = [{ value: minAmount, step: stepAmount }, { value: maxAmount }]

  const rangeDurationParams = [{ value: minDuration, step: stepDuration }, { value: maxDuration }]

  return (
    <div data-rf={RF_TABS.SAVINGS_INPUTS}>
      <div className='csas-calc-row csas-half-in-mobile'>
        <InvestorSelect
          label={t('SAVINGS_INPUTS.INVESTMENT_TYPE_LABEL')}
          selectedType={savingsInputs.investorType}
          onSelect={handleInvestmentTypeChange}
        />
      </div>
      <div className='csas-calc-row csas-half-in-mobile'>
        <div className='csas-calc-controls-label csas-calc-controls-label-fixed-medium'>
          {t('SAVINGS_INPUTS.AMOUNT_LABEL')}
        </div>
        <div className='csas-calc-controls-group visible-lg-inline-block csas-range-slider'>
          <RangeSlider
            minLabel={thousandsFormatter(minAmount) + ' ' + t('CZK')}
            maxLabel={thousandsFormatter(maxAmount) + ' ' + t('CZK')}
            value={currentAmount}
            range={rangeAmountParams}
            onChange={handleCurrentAmountChange}
            onChangeComplete={handleAmountChange}
            userTracking={userTracking}
            trackElement='amount'
            rf={RF_SAVINGS_INPUTS.AMOUNT_SLIDER}
          />
        </div>
        <PlusMinusInput
          unit={t('CZK')}
          value={currentAmount}
          defaultValue={defaultAmount}
          min={minAmount}
          max={maxAmount}
          format={thousandsFormatter}
          elementClass='csas-calc-controls-group'
          outerClass='csas-calc-input csas-calc-input--long'
          inputClass='csas-input-tel'
          onMinus={handleMinusAmount}
          onPlus={handlePlusAmount}
          onChangeComplete={handleAmountChange}
          userTracking={userTracking}
          rfMinus={RF_SAVINGS_INPUTS.AMOUNT_MINUS}
          rfPlus={RF_SAVINGS_INPUTS.AMOUNT_PLUS}
          rf={RF_SAVINGS_INPUTS.AMOUNT_INPUT}
        />
      </div>
      <div className='csas-calc-row csas-half-in-mobile'>
        <div className='csas-calc-controls-label csas-calc-controls-label-fixed-medium'>
          {t('SAVINGS_INPUTS.DURATION_LABEL')}
        </div>
        <div className='csas-calc-controls-group visible-lg-inline-block csas-range-slider'>
          <RangeSlider
            minLabel={minDuration + ' ' + t('PLURAL.YEARS', { smart_count: minDuration })}
            maxLabel={maxDuration + ' ' + t('PLURAL.YEARS', { smart_count: maxDuration })}
            value={currentDuration}
            range={rangeDurationParams}
            onChange={handleCurrentDurationChange}
            onChangeComplete={handleDurationChange}
            userTracking={userTracking}
            trackElement='amount'
            rf={RF_SAVINGS_INPUTS.DURATION_SLIDER}
          />
        </div>
        <PlusMinusInput
          unit={t('PLURAL.YEARS', { smart_count: currentDuration })}
          value={currentDuration}
          defaultValue={defaultDuration}
          min={minDuration}
          max={maxDuration}
          format={thousandsFormatter}
          elementClass='csas-calc-controls-group'
          outerClass='csas-calc-input csas-calc-input--long'
          inputClass='csas-input-tel'
          onMinus={handleMinusDuration}
          onPlus={handlePlusDuration}
          onChangeComplete={handleDurationChange}
          userTracking={userTracking}
          rfMinus={RF_SAVINGS_INPUTS.DURATION_MINUS}
          rfPlus={RF_SAVINGS_INPUTS.DURATION_PLUS}
          rf={RF_SAVINGS_INPUTS.DURATION_INPUT}
        />
      </div>
      <div className='csas-calc-row csas-mt-xs'>
        <TipsRotator tips={t('HINT.TEXT')} label={t('HINT.LABEL')} />
      </div>
    </div>
  )
}

export default SavingsInputs
